import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { ApiError } from '@/api/errors';
import { queryKeys } from '@/api/queryKeys';
import type { UserDto } from '@/api/types';
import { useSessionStore } from '@/stores/session';

import * as authApi from './api';

/**
 * The signed-in user, or null.
 *
 * Runs only when a token exists, so a signed-out visitor never fires a request
 * that is guaranteed to 401.
 */
export function useCurrentUser() {
    const token = useSessionStore((state) => state.token);
    const finishRestoring = useSessionStore((state) => state.finishRestoring);

    const query = useQuery({
        queryKey: queryKeys.session.current,
        queryFn: authApi.fetchCurrentUser,
        enabled: token !== null,
        // The client already drops the session on a 401, so retrying an
        // expired token only delays showing the login screen.
        retry: false,
        staleTime: 60_000,
    });

    // Once the boot request settles, routing may proceed. The API client has
    // already cleared the session if the token turned out to be dead.
    useEffect(() => {
        if (token === null || query.isSuccess || query.isError) {
            finishRestoring();
        }
    }, [token, query.isSuccess, query.isError, finishRestoring]);

    return query;
}

/**
 * Whether the app still has to work out who the visitor is.
 * Routing must wait for this to avoid bouncing a signed-in user to /inloggen.
 */
export function useIsRestoringSession(): boolean {
    return useSessionStore((state) => state.isRestoring);
}

function useAdoptSession() {
    const setToken = useSessionStore((state) => state.setToken);
    const queryClient = useQueryClient();

    return (token: string, user: UserDto) => {
        setToken(token);
        // Seeding the cache means the app renders the dashboard immediately
        // instead of flashing a loading state for data we already hold.
        queryClient.setQueryData(queryKeys.session.current, user);
    };
}

export function useLogin() {
    const adoptSession = useAdoptSession();

    return useMutation({
        mutationFn: (payload: authApi.LoginPayload) =>
            authApi.login({ ...payload, device_name: authApi.currentDeviceName() }),
        onSuccess: (result) => {
            adoptSession(result.token, result.user);
        },
    });
}

export function useRegister() {
    const adoptSession = useAdoptSession();

    return useMutation({
        mutationFn: (payload: Omit<authApi.RegisterPayload, 'device_name' | 'timezone'>) =>
            authApi.register({
                ...payload,
                device_name: authApi.currentDeviceName(),
                // Reminders are scheduled in the user's own timezone, so it is
                // captured at registration rather than guessed later.
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            }),
        onSuccess: (result) => {
            adoptSession(result.token, result.user);
        },
    });
}

export function useLogout() {
    const clear = useSessionStore((state) => state.clear);
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: authApi.logout,
        // Runs on success *and* on failure: if the token was already invalid
        // server-side, the user must still end up signed out locally.
        onSettled: () => {
            clear();
            queryClient.clear();
        },
    });
}

export function useRequestPasswordReset() {
    return useMutation({
        mutationFn: (email: string) => authApi.requestPasswordReset(email),
    });
}

export function useResetPassword() {
    return useMutation({
        mutationFn: (payload: authApi.ResetPasswordPayload) => authApi.resetPassword(payload),
    });
}

export function useResendVerificationEmail() {
    return useMutation({
        mutationFn: authApi.resendVerificationEmail,
    });
}

/**
 * Field-level messages from a failed mutation.
 *
 * The backend returns Dutch messages keyed by field name; forms render them
 * inline rather than re-implementing validation in the client.
 */
export function fieldErrorsFrom(error: unknown): Record<string, string> {
    if (!(error instanceof ApiError) || !error.isValidation) {
        return {};
    }

    return Object.fromEntries(
        Object.entries(error.fieldErrors)
            .map(([field, messages]) => [field, messages[0]])
            .filter((entry): entry is [string, string] => entry[1] !== undefined),
    );
}

/**
 * The message to show above a form, or undefined when the failure is purely
 * field-level and already shown inline.
 */
export function formErrorFrom(error: unknown): string | undefined {
    if (error === null || error === undefined) {
        return undefined;
    }

    if (error instanceof ApiError) {
        return error.isValidation ? undefined : error.message;
    }

    return 'Er ging iets mis. Probeer het opnieuw.';
}
